import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useSWR from "swr";
import { visionService } from "@/services/vision-service";
import type {
  ProposalListItem,
  ProposalListParams,
} from "@/services/vision-service/types";
import { format, subDays } from "date-fns";

function defaultDateRange() {
  const to = new Date();
  const from = subDays(to, 6);
  return {
    date_from: format(from, "yyyy-MM-dd"),
    date_to: format(to, "yyyy-MM-dd"),
  };
}

const PAGE_SIZE = 10;
const STORAGE_KEY = "proposal-list-state";
const SEARCH_DEBOUNCE_MS = 600;
const SLIDER_DEBOUNCE_MS = 800;

export interface ProposalFiltersState {
  search: string;
  industry: string;
  city: string;
  contract_months: string;
  min_capacity: string;
  max_capacity: string;
  created_by_user_id: string;
  date_from: string;
  date_to: string;
  has_license_fee: boolean;
  has_flexible_credits: boolean;
}

export type ProposalSortField = "createdAt" | "averageMonthlyValue";
export type ProposalSortDir = "asc" | "desc";

export interface ProposalSortState {
  field: ProposalSortField;
  dir: ProposalSortDir;
}

export interface ProposalListContextValue {
  proposals: ProposalListItem[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  error: Error | null;
  filters: ProposalFiltersState;
  sort: ProposalSortState;
  activePage: number;
  pageSize: number;
  updateFilters: (partial: Partial<ProposalFiltersState>) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  toggleSort: (field: ProposalSortField) => void;
  refetch: () => void;
}

const EMPTY_FILTERS: ProposalFiltersState = {
  search: "",
  industry: "",
  city: "",
  contract_months: "",
  min_capacity: "",
  max_capacity: "",
  created_by_user_id: "",
  date_from: "",
  date_to: "",
  has_license_fee: false,
  has_flexible_credits: false,
};

const DEFAULT_FILTERS: ProposalFiltersState = {
  ...EMPTY_FILTERS,
  ...defaultDateRange(),
};

const DEFAULT_SORT: ProposalSortState = { field: "createdAt", dir: "desc" };

function loadStoredState(): {
  filters: ProposalFiltersState;
  sort: ProposalSortState;
  activePage: number;
} {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw)
      return { filters: DEFAULT_FILTERS, sort: DEFAULT_SORT, activePage: 1 };
    const parsed = JSON.parse(raw);
    return {
      filters: { ...DEFAULT_FILTERS, ...parsed.filters },
      sort: { ...DEFAULT_SORT, ...parsed.sort },
      activePage: parsed.activePage ?? 1,
    };
  } catch {
    return { filters: DEFAULT_FILTERS, sort: DEFAULT_SORT, activePage: 1 };
  }
}

function saveState(
  filters: ProposalFiltersState,
  sort: ProposalSortState,
  activePage: number,
) {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ filters, sort, activePage }),
    );
  } catch (_) {
    // quota exceeded or private browsing — ignore
  }
}

function buildParams(
  filters: ProposalFiltersState,
  debouncedSearch: string,
  debouncedCapacity: { min: string; max: string },
  sort: ProposalSortState,
  activePage: number,
): ProposalListParams {
  const p: ProposalListParams = {
    skip: (activePage - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
    order_by:
      sort.field === "createdAt" ? "created_at" : "average_monthly_cost",
    order_direction: sort.dir,
  };
  if (debouncedSearch) p.search = debouncedSearch;
  if (filters.industry) p.industry = filters.industry;
  if (filters.city) p.city = filters.city;
  if (filters.contract_months)
    p.contract_months = Number(filters.contract_months);
  if (debouncedCapacity.min) p.min_capacity = Number(debouncedCapacity.min);
  if (debouncedCapacity.max) p.max_capacity = Number(debouncedCapacity.max);
  if (filters.created_by_user_id)
    p.created_by_user_id = Number(filters.created_by_user_id);
  if (filters.date_from) p.date_from = filters.date_from;
  if (filters.date_to) p.date_to = filters.date_to;
  if (filters.has_license_fee) p.has_license_fee = true;
  if (filters.has_flexible_credits) p.has_flexible_credits = true;
  return p;
}

const ProposalListContext = createContext<ProposalListContextValue | undefined>(
  undefined,
);

export const ProposalListProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const stored = useMemo(() => loadStoredState(), []);

  const [filters, setFilters] = useState<ProposalFiltersState>(stored.filters);
  const [sort, setSort] = useState<ProposalSortState>(stored.sort);
  const [activePage, setActivePage] = useState(stored.activePage);
  const [debouncedSearch, setDebouncedSearch] = useState(stored.filters.search);
  const [debouncedCapacity, setDebouncedCapacity] = useState({
    min: stored.filters.min_capacity,
    max: stored.filters.max_capacity,
  });

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sliderDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setActivePage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [filters.search]);

  useEffect(() => {
    if (sliderDebounceRef.current) clearTimeout(sliderDebounceRef.current);
    sliderDebounceRef.current = setTimeout(() => {
      setDebouncedCapacity({
        min: filters.min_capacity,
        max: filters.max_capacity,
      });
      setActivePage(1);
    }, SLIDER_DEBOUNCE_MS);
    return () => {
      if (sliderDebounceRef.current) clearTimeout(sliderDebounceRef.current);
    };
  }, [filters.min_capacity, filters.max_capacity]);

  const params = useMemo(
    () =>
      buildParams(
        filters,
        debouncedSearch,
        debouncedCapacity,
        sort,
        activePage,
      ),
    [filters, debouncedSearch, debouncedCapacity, sort, activePage],
  );

  const { data, isLoading, error, mutate } = useSWR(
    ["/api/v1/proposals", params],
    ([, p]) => visionService.listProposals(p),
    { keepPreviousData: true },
  );

  useEffect(() => {
    saveState(filters, sort, activePage);
  }, [filters, sort, activePage]);

  const updateFilters = useCallback(
    (partial: Partial<ProposalFiltersState>) => {
      setFilters((prev) => ({ ...prev, ...partial }));
      const onlyDebouncedFields = Object.keys(partial).every(
        (k) => k === "search" || k === "min_capacity" || k === "max_capacity",
      );
      if (!onlyDebouncedFields) setActivePage(1);
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setDebouncedSearch("");
    setDebouncedCapacity({ min: "", max: "" });
    setActivePage(1);
  }, []);

  const setPage = useCallback((page: number) => setActivePage(page), []);

  const toggleSort = useCallback((field: ProposalSortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "desc" },
    );
    setActivePage(1);
  }, []);

  const refetch = useCallback(() => {
    mutate();
  }, [mutate]);

  const value = useMemo<ProposalListContextValue>(() => {
    const proposals = data?.proposals ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    return {
      proposals,
      total,
      totalPages,
      isLoading,
      error:
        error instanceof Error
          ? error
          : error
            ? new Error("Failed to load proposals")
            : null,
      filters,
      sort,
      activePage,
      pageSize: PAGE_SIZE,
      updateFilters,
      clearFilters,
      setPage,
      toggleSort,
      refetch,
    };
  }, [
    data,
    isLoading,
    error,
    filters,
    sort,
    activePage,
    updateFilters,
    clearFilters,
    setPage,
    toggleSort,
    refetch,
  ]);

  return (
    <ProposalListContext.Provider value={value}>
      {children}
    </ProposalListContext.Provider>
  );
};

export const useProposalList = (): ProposalListContextValue => {
  const context = useContext(ProposalListContext);
  if (!context) {
    throw new Error(
      "useProposalList must be used within a ProposalListProvider",
    );
  }
  return context;
};
